import { useCallback, useEffect, useState } from 'react'

import {
  fetchTodayAttendance,
  submitAttendance,
  uploadAttendancePhoto,
  type AttendanceAction,
  type WarehouseAttendanceStaff,
} from '@/features/warehouse/api/attendanceApi'
import {
  getWarehouseSession,
  updateWarehouseSessionAuthority,
} from '@/shared/auth/warehouseSessionStorage'
import { EmptyState } from '@/shared/components/EmptyState'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { SkeletonList } from '@/shared/components/Skeleton'
import { Toast } from '@/shared/components/Toast'

type SelectedAttendance = {
  staff: WarehouseAttendanceStaff
  action: AttendanceAction
}

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
}

function staffStatus(staff: WarehouseAttendanceStaff): 'belum' | 'masuk' | 'selesai' {
  if (!staff.check_in_at) return 'belum'
  return staff.check_out_at ? 'selesai' : 'masuk'
}

function statusLabel(status: ReturnType<typeof staffStatus>): string {
  if (status === 'belum') return 'Belum absen'
  if (status === 'masuk') return 'Sedang bertugas'
  return 'Selesai'
}

function statusClass(status: ReturnType<typeof staffStatus>): string {
  if (status === 'belum') return 'badge-draft'
  if (status === 'masuk') return 'badge-warning'
  return 'badge-success'
}

export function WarehouseAttendancePage() {
  const [staff, setStaff] = useState<WarehouseAttendanceStaff[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [selected, setSelected] = useState<SelectedAttendance | null>(null)
  const [photo, setPhoto] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [pinOpen, setPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    fetchTodayAttendance()
      .then((items) => {
        if (!cancelled) setStaff(items)
      })
      .catch(() => {
        if (!cancelled) setLoadError('Gagal memuat absensi hari ini.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => reload(), [reload, refreshKey])
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  function resetForm() {
    setSelected(null)
    setPhoto(null)
    setPreviewUrl(null)
    setFormError(null)
    setPinOpen(false)
  }

  function chooseAction(member: WarehouseAttendanceStaff) {
    const action: AttendanceAction = staffStatus(member) === 'belum' ? 'check_in' : 'check_out'
    setSelected({ staff: member, action })
    setPhoto(null)
    setPreviewUrl(null)
    setFormError(null)
  }

  function handlePhotoChange(file: File | null) {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPhoto(file)
    setPreviewUrl(file ? URL.createObjectURL(file) : null)
    setFormError(null)
  }

  function openPin() {
    if (!photo) {
      setFormError('Ambil selfie terlebih dahulu.')
      return
    }
    setPinOpen(true)
  }

  async function confirmAttendance(pin: string) {
    if (!selected || !photo) throw new Error('Data absensi belum lengkap.')

    const uploaded = await uploadAttendancePhoto(photo, crypto.randomUUID())
    const result = await submitAttendance(selected.action, {
      userId: selected.staff.id,
      pin,
      photoUrl: uploaded.key,
    })

    if (result.authority || typeof result.head_present === 'boolean') {
      const session = getWarehouseSession()
      updateWarehouseSessionAuthority(
        result.authority ?? session?.authority ?? 'attendance_only',
        result.head_present ?? session?.headPresent ?? false,
      )
    }

    setStaff((current) =>
      current.map((member) => {
        if (member.id !== selected.staff.id) return member
        return selected.action === 'check_in'
          ? { ...member, check_in_at: result.recorded_at, check_in_photo: uploaded.url, on_time: result.on_time }
          : { ...member, check_out_at: result.recorded_at, check_out_photo: uploaded.url }
      }),
    )
    setToast(`${selected.staff.name} berhasil ${selected.action === 'check_in' ? 'check-in' : 'check-out'} pada ${formatTime(result.recorded_at)}.`)
    resetForm()
  }

  const selectedActionLabel = selected?.action === 'check_out' ? 'Check-out' : 'Check-in'

  return (
    <section className="space-y-4">
      <header className="card flex items-start justify-between gap-3">
        <div>
          <h1 className="page-title">Absensi gudang</h1>
          <p className="text-body-sm text-on-surface-variant mt-1">Ambil selfie dan konfirmasi PIN pribadi untuk absensi.</p>
        </div>
        <button
          type="button"
          className="btn-outline min-w-[44px] px-3"
          onClick={() => setRefreshKey((key) => key + 1)}
          disabled={isLoading}
          aria-label="Muat ulang absensi"
        >
          <span className={`material-symbols-outlined ${isLoading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
      </header>

      {isLoading && <SkeletonList count={3} label="Memuat absensi…" />}
      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span>{loadError}</span>
          <button type="button" className="btn-outline min-h-[36px] text-body-sm" onClick={() => setRefreshKey((key) => key + 1)}>
            Coba lagi
          </button>
        </div>
      )}

      {!isLoading && !loadError && staff.length === 0 && (
        <EmptyState icon="groups" title="Belum ada staf gudang" description="Hubungi Admin HO untuk mengatur akses staf gudang." />
      )}

      {!isLoading && !loadError && !selected && staff.map((member) => {
        const status = staffStatus(member)
        return (
          <article key={member.id} className="card space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-body-lg font-bold text-on-surface">{member.name}</p>
                <p className="text-body-sm text-on-surface-variant">{member.role_label} · {member.shift_start}–{member.shift_end}</p>
              </div>
              <span className={statusClass(status)}>{statusLabel(status)}</span>
            </div>
            {member.check_in_at && (
              <p className="text-body-sm text-on-surface-variant">
                Masuk <strong className="font-mono text-on-surface">{formatTime(member.check_in_at)}</strong>
                {member.check_out_at && <> · Keluar <strong className="font-mono text-on-surface">{formatTime(member.check_out_at)}</strong></>}
              </p>
            )}
            {status !== 'selesai' && (
              <button type="button" className={status === 'belum' ? 'btn-primary w-full' : 'btn-outline w-full'} onClick={() => chooseAction(member)}>
                {status === 'belum' ? 'Check-in' : 'Check-out'}
              </button>
            )}
          </article>
        )
      })}

      {selected && (
        <div className="card space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-headline-md text-primary">{selectedActionLabel}</h2>
              <p className="text-body-sm text-on-surface-variant">{selected.staff.name} · {selected.staff.role_label}</p>
            </div>
            <button type="button" className="btn-outline min-h-[44px]" onClick={resetForm}>Batal</button>
          </div>
          <label className="field-label" htmlFor="warehouse_attendance_selfie">Selfie</label>
          <input
            id="warehouse_attendance_selfie"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="user"
            className="input-field"
            onChange={(event) => handlePhotoChange(event.target.files?.[0] ?? null)}
          />
          {previewUrl && <img src={previewUrl} alt={`Selfie ${selected.staff.name}`} className="w-full aspect-[4/3] object-cover rounded-lg border border-outline-variant" />}
          {formError && <div role="alert" className="alert-error">{formError}</div>}
          <button type="button" className="btn-primary w-full" onClick={openPin}>Lanjutkan dengan PIN</button>
        </div>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={`Konfirmasi ${selectedActionLabel.toLowerCase()}`}
        description={`Masukkan PIN ${selected?.staff.name ?? 'staf'} untuk menyelesaikan absensi.`}
        confirmLabel="Simpan absensi"
        onClose={() => setPinOpen(false)}
        onConfirm={confirmAttendance}
      />
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </section>
  )
}
